import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Keep-alive component to prevent Fire TV Stick from sleeping
const KeepAlive = () => {
  const canvasRef = useRef(null);
  const frameRef = useRef(0);

  useEffect(() => {
    let animationId;
    let intervalId;
    
    // Method 1: Canvas animation (invisible activity)
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      
      const animate = () => {
        frameRef.current = (frameRef.current + 1) % 360;
        ctx.clearRect(0, 0, 1, 1);
        ctx.fillStyle = `rgba(0,0,0,${0.001 * (frameRef.current % 10)})`;
        ctx.fillRect(0, 0, 1, 1);
        animationId = requestAnimationFrame(animate);
      };
      animate();
    }

    // Method 2: Periodic DOM micro-update
    intervalId = setInterval(() => {
      if (canvas) {
        canvas.setAttribute('data-tick', Date.now());
      }
    }, 5000);

    // Method 3: Wake Lock API
    let wakeLock = null;
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
        }
      } catch (err) {}
    };
    requestWakeLock();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Method 4: Periodic activity
    const videoKeepAlive = setInterval(() => {
      const _ = performance.now();
      if (canvas) void canvas.offsetHeight;
    }, 10000);

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      if (intervalId) clearInterval(intervalId);
      if (videoKeepAlive) clearInterval(videoKeepAlive);
      if (wakeLock) wakeLock.release();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={1}
      height={1}
      style={{
        position: 'fixed',
        top: -10,
        left: -10,
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
        zIndex: -9999,
      }}
      aria-hidden="true"
    />
  );
};

const SlideshowPublicPage = ({ slideshowData: propSlideshowData }) => {
  const { slideshowId } = useParams();
  const [slideshow, setSlideshow] = useState(propSlideshowData || null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(!propSlideshowData);
  const [error, setError] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const fetchSlideshow = useCallback(async () => {
    // Skip fetching if data was provided via props
    if (propSlideshowData) {
      setSlideshow(propSlideshowData);
      setIsLoading(false);
      return;
    }
    
    if (!slideshowId) return;
    
    try {
      const response = await axios.get(`${API}/public/slideshows/${slideshowId}`);
      setSlideshow(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al cargar el slideshow");
    } finally {
      setIsLoading(false);
    }
  }, [slideshowId, propSlideshowData]);

  useEffect(() => {
    fetchSlideshow();
    // Auto-refresh slideshow data every 60 seconds
    const refreshInterval = setInterval(fetchSlideshow, 60000);
    return () => clearInterval(refreshInterval);
  }, [fetchSlideshow]);

  // Auto-rotate menus
  useEffect(() => {
    if (!slideshow?.menus_data || slideshow.menus_data.length <= 1) return;

    const currentMenu = slideshow.menus_data[currentIndex];
    const duration = (currentMenu?.slideshow_duration || slideshow.global_duration || 10) * 1000;

    const timer = setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % slideshow.menus_data.length);
        setIsTransitioning(false);
      }, 500); // Transition duration
    }, duration);

    return () => clearTimeout(timer);
  }, [slideshow, currentIndex]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  // Handle suspended user
  if (slideshow?.suspended) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-900 to-stone-800 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Servicio Suspendido</h1>
          <p className="text-stone-300">{slideshow.suspension_message}</p>
        </div>
      </div>
    );
  }

  // Handle outside schedule
  if (slideshow?.outside_schedule) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-900 to-stone-800 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <Clock className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Fuera de Horario</h1>
          <p className="text-stone-300">{slideshow.fallback_message}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-900 to-stone-800 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Error</h1>
          <p className="text-stone-300">{error}</p>
        </div>
      </div>
    );
  }

  if (!slideshow?.menus_data || slideshow.menus_data.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-900 to-stone-800 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Sin Menús</h1>
          <p className="text-stone-300">Este slideshow no tiene menús configurados.</p>
        </div>
      </div>
    );
  }

  const currentMenu = slideshow.menus_data[currentIndex];
  const transitionClass = slideshow.transition_effect === "fade" 
    ? "transition-opacity duration-500" 
    : slideshow.transition_effect === "slide"
    ? "transition-transform duration-500"
    : "";

  return (
    <div className="min-h-screen bg-black overflow-hidden">
      {/* Keep-alive component to prevent Fire TV Stick from sleeping */}
      <KeepAlive />
      
      {/* Menu display */}
      <div 
        className={`relative w-full h-screen ${transitionClass}`}
        style={{
          opacity: isTransitioning && slideshow.transition_effect === "fade" ? 0 : 1,
          transform: isTransitioning && slideshow.transition_effect === "slide" ? "translateX(-100%)" : "translateX(0)"
        }}
      >
        {/* Canvas container */}
        <div
          className="relative mx-auto h-full"
          style={{
            aspectRatio: `${currentMenu.canvas_width || 1920} / ${currentMenu.canvas_height || 1080}`,
            maxWidth: "100vw",
            maxHeight: "100vh",
            backgroundImage: currentMenu.background_image ? `url(${currentMenu.background_image})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundColor: currentMenu.background_image ? undefined : "#1a1a1a"
          }}
        >
          {/* Render menu items */}
          {currentMenu.items?.map((item) => (
            <div
              key={item.id}
              className="absolute"
              style={{
                left: `${item.x}%`,
                top: `${item.y}%`,
                zIndex: item.z_index || 0,
                opacity: item.opacity ?? 1
              }}
            >
              {item.type === "image" && item.image_url ? (
                <img
                  src={item.image_url}
                  alt={item.label || ""}
                  style={{
                    width: `${(item.width / 100) * (currentMenu.canvas_width || 1920)}px`,
                    height: `${(item.height / 100) * (currentMenu.canvas_height || 1080)}px`,
                    objectFit: "contain"
                  }}
                  draggable={false}
                />
              ) : (
                <span
                  style={{
                    fontSize: `${item.font_size || 18}px`,
                    fontFamily: item.font_family || "Inter",
                    color: item.color || "#ffffff",
                    fontWeight: item.font_weight || 400,
                    textShadow: item.text_shadow ? "2px 2px 4px rgba(0,0,0,0.8)" : "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  {item.text || item.name || item.price || ""}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SlideshowPublicPage;
